import React, {useState} from "react";
import {TouchableOpacity, View} from "react-native";
import StandardText from "../standardText/StandardText";
import GameHelper from "../../game/GameHelper";
import {format, parseISO} from 'date-fns'
import Colors from "../../styles/colors";
import ChevronIcon from "../../icons/ChevronIcon";
import ClubsIcon from "../../game/components/suits/ClubsIcon";
import HeartsIcon from "../../game/components/suits/HeartsIcon";
import DiamondsIcon from "../../game/components/suits/DiamondsIcon";
import SpadesIcon from "../../game/components/suits/SpadesIcon";
import ShuffleIcon from "../../icons/ShuffleIcon";
import PlayerBadge from "../../game/components/PlayerBadge";
import LoadingButton from "../loadingButton/LoadingButton";
import GameConfigIcons from "../../game/components/GameConfigIcons";
import { secondsAsTimer, timerString, useOpenGameTimers } from "../../hooks/useOpenGameTimers";

const GameItem = ({game, onPress, onArchiveTogglePress, onRemoveTogglePress, enablePopups = false, disablePress = false}) => {

    const [toggleIndex, setToggleIndex] = useState(0);
    const { gameExpiresSeconds } = useOpenGameTimers(game);
    const gameExpiresTimer = typeof gameExpiresSeconds === "number"
        ? secondsAsTimer(gameExpiresSeconds, "hours")
        : null;

    const isOpenGame = game.isOpenGame;
    const isGameStarted = game.hasStarted;
    const isViewerInGame = !!GameHelper.getViewerPlayer(game);
    const totalPlayers = GameHelper.getAllPlayers(game).length;
    const isViewerTurn = isViewerInGame ? GameHelper.getViewerPlayer(game).isCurrentTurn : false;
    const currentTurnPlayer = GameHelper.getAllPlayers(game).find((player) => player.isCurrentTurn);
    const isDailyTournamentGame = !!game.duplicateConfigGame;
    const createdAt = format(parseISO(game.createdAt), "MM/dd/yyyy 'at' HH:mm");
    const endedAt = game.endDate ? format(parseISO(game.endDate), "MM/dd/yyyy 'at' HH:mm") : null;
    const openGameTeamCounts = [];

    if (isOpenGame && !isGameStarted) {
        for (const team of game.gameTeams) {
            openGameTeamCounts.push(team.teamPlayers.length);
        }
    }

    const addThousandsCommas = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const viewerTeamScore = !isViewerInGame || (isOpenGame && !isGameStarted) ? 0 : GameHelper.getViewerTeam(game).gameTeamScores.reduce(
        (acc, score) => acc + score.bonusCount + score.baseCount + score.secondCount, 0
    );
    const opponentTeamScore = !isViewerInGame || (isOpenGame && !isGameStarted) ? 0 : GameHelper.getOpponentTeam(game).gameTeamScores.reduce(
        (acc, score) => acc + score.bonusCount + score.baseCount + score.secondCount, 0
    );

    const partnerPlayer = isViewerInGame && GameHelper.getViewerPartnerPlayer(game);
    const opponentPlayer1 = isViewerInGame && GameHelper.getOpponentTeam(game).teamPlayers[0];
    const opponentPlayer2 = isViewerInGame && totalPlayers > 2 ? GameHelper.getOpponentTeam(game).teamPlayers[1] : null;
    const viewerScoreText = `${totalPlayers > 2 && partnerPlayer ? `You and ${partnerPlayer.firstName}` : 'You'}: ${addThousandsCommas(viewerTeamScore)}`;
    const opponentScoreText = opponentPlayer1 ? `${opponentPlayer1.firstName} ${totalPlayers > 2 && opponentPlayer2 ? `and ${opponentPlayer2.firstName}` : ''}: ${addThousandsCommas(opponentTeamScore)}` : '';
    const itemColor = isDailyTournamentGame ? Colors.tournamentColor : (isViewerTurn ? 'rgba(136, 80, 255, 1)' : 'rgba(248, 121, 80, 1)');
    const organizerColor = 'rgba(248, 181, 80, 1)';
    const togglableItems = isViewerTurn ? 5 : (game.hasEnded ? 3 : 4);
    const disabled = game.isOpenGame && !game.hasStarted && (
        game.abandoned ||
        game.expired ||
        (game.expiresAt && (new Date(game.expiresAt)).getTime() <= Date.now())
    );

    const leftTeam = !isViewerInGame || (isOpenGame && !isGameStarted) ? GameHelper.getTeamById(game, 0) : GameHelper.getViewerTeam(game);
    const rightTeam = !isViewerInGame || (isOpenGame && !isGameStarted) ? GameHelper.getTeamById(game, 1) : GameHelper.getOpponentTeam(game);

    const handleTogglePress = () => {
        setToggleIndex((v) => {
            return v === togglableItems - 1 ? 0 : v + 1;
        });
    };

    const translateToggleIndex = (index) => {
        if (isOpenGame && !isGameStarted) return -1;
        return isViewerTurn ? index : index + 1;
    };

    const getSuitIcon = () => {
        if (isDailyTournamentGame) return null;
        const i = game.alphanumericId.charCodeAt(0) % 4;
        const color = isViewerTurn ? 'rgba(136, 59, 229, 1)' : 'rgba(255, 155, 126, 1)';
        const size = 200;
        switch (i) {
            case 0:
                return <ClubsIcon color={color} size={size}/>;
            case 1:
                return <DiamondsIcon color={color} size={size}/>;
            case 2:
                return <HeartsIcon color={color} size={size}/>;
            case 3:
                return <SpadesIcon color={color} size={size}/>;
        }
    };

    return (
        <View style={{ opacity: disabled ? 0.7 : 1.0 }}>
            <View style={{...ownStyles.itemContainer, ...(isDailyTournamentGame ? ownStyles.dailyTournamentGameContainer : (isViewerTurn ? ownStyles.currentTurnContainer : {})), ...((game.isOpenGame && !isGameStarted) ? ownStyles.openGameContainer : {})}}>
                {!(game.isOpenGame && !isGameStarted) && (
                    <View style={{position: 'absolute', top: -50, right: 10}}>
                        {getSuitIcon()}
                    </View>
                )}
                <TouchableOpacity onPress={handleTogglePress} style={{justifyContent: 'center'}} disabled={disabled}>
                    {(isOpenGame && !isGameStarted) && (
                        <View style={{flexDirection: 'column', alignItems: 'start'}}>
                            <View style={{flexDirection: 'row', alignItems: 'center'}}>
                                <StandardText style={{...ownStyles.itemText}}>{isDailyTournamentGame ? game.duplicateConfigGameName : `Game ${game.id}`}</StandardText>
                                <StandardText style={{...ownStyles.itemTextSmallMuted, marginLeft: 8}}>
                                    {gameExpiresTimer && `(expires ${timerString([gameExpiresTimer.hours, gameExpiresTimer.minutes, gameExpiresTimer.seconds])})`}
                                </StandardText>
                            </View>
                            <GameConfigIcons game={game} iconColor={Colors.onPrimary}/>
                        </View>
                    )}

                    {translateToggleIndex(toggleIndex) === 0 && (
                        <View style={{...ownStyles.yourTurnButton}}>
                            <StandardText style={{weight: 500}}>Your turn!</StandardText>
                        </View>
                    )}
                    {translateToggleIndex(toggleIndex) === 1 && (
                        <View>
                            <StandardText style={{...ownStyles.itemText}}>{isDailyTournamentGame ? game.duplicateConfigGameName : `Game ${game.id}`}</StandardText>
                            {(!game.hasEnded || game.winnerTeam?.id === GameHelper.getViewerTeam(game).id) && (
                                <StandardText
                                    style={{...ownStyles.itemText, ...ownStyles.itemTextSub}}>
                                        {game.hasEnded && game.winnerTeam?.id === GameHelper.getViewerTeam(game).id ? `YOU WON!` : `Round ${GameHelper.getRoundPoints(game.currentRound)}`}
                                </StandardText>
                            )}
                        </View>
                    )}
                    {translateToggleIndex(toggleIndex) === 2 && (
                        <View>
                            <StandardText style={{...ownStyles.itemText}}>Started: {createdAt}</StandardText>
                            {game.hasEnded ? (
                                <StandardText style={{...ownStyles.itemText}}>Ended: {endedAt}</StandardText>
                            ) : (
                                <StandardText style={{...ownStyles.itemText, ...ownStyles.itemTextSub}}>
                                    Next
                                    Turn: {isViewerTurn ? 'You' : (currentTurnPlayer ? (currentTurnPlayer.firstName) : 'Start Round ' + GameHelper.getRoundPoints(game.currentRound))}
                                </StandardText>
                            )}
                            
                        </View>
                    )}
                    {translateToggleIndex(toggleIndex) === 3 && (
                        <View>
                            <StandardText style={{...ownStyles.itemText, ...ownStyles.itemTextSub}}>
                                {viewerScoreText}
                            </StandardText>
                            <StandardText style={{...ownStyles.itemText, ...ownStyles.itemTextSub}}>
                                {opponentScoreText}
                            </StandardText>
                        </View>
                    )}
                    {translateToggleIndex(toggleIndex) === 4 && (
                        <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', flex: 1}}>
                            <View style={{marginRight: 6}}>
                                <LoadingButton
                                    onPress={onArchiveTogglePress}
                                    label={GameHelper.getViewerPlayer(game).archived ? 'Move to Active' : 'Archive'}
                                    showChevron={false}
                                    backgroundColor={Colors.background}
                                    labelColor={Colors.onBackground}
                                />
                            </View>
                            <LoadingButton
                                onPress={onRemoveTogglePress}
                                label={'Delete'}
                                showChevron={false}
                                backgroundColor={Colors.background}
                                labelColor={Colors.onBackground}
                            />
                            <TouchableOpacity onPress={handleTogglePress} style={{marginStart: 10}} disabled={disabled}>
                                <StandardText style={{color: Colors.onPrimary}}>Cancel</StandardText>
                            </TouchableOpacity>
                        </View>
                    )}
                </TouchableOpacity>

                <TouchableOpacity style={{ flex: 1 }} onPress={() => onPress()} disabled={(isOpenGame && !isViewerInGame) || disabled} />


                <TouchableOpacity onPress={() => onPress()} disabled={(isOpenGame && !isViewerInGame) || disabled}
                                  style={{flexDirection: 'row', alignItems: 'center', paddingLeft: 20}}>
                    {leftTeam.teamPlayers.map((player) => (
                        <View style={{ marginRight: 2 }} key={player.id}>
                            <PlayerBadge
                                player={player}
                                showBottomInfo={false}
                                active={player.isCurrentTurn || ((isOpenGame && !isGameStarted) && GameHelper.isOrganizingTeamPlayer(game, player))}
                                activePlainColor={(isOpenGame && !isGameStarted) && GameHelper.isOrganizingTeamPlayer(game, player) ? organizerColor : itemColor}
                                bare={(isOpenGame && !isGameStarted) && !GameHelper.isOrganizingTeamPlayer(game, player)}
                                popupEnabled={enablePopups}
                                animate={!isOpenGame || isGameStarted}
                                menuDisplayNameSubtitle={isOpenGame && !isGameStarted && GameHelper.isOrganizingTeamPlayer(game, player) ? 'Game Organizer' : null}
                            />
                        </View>
                    ))}

                    {isOpenGame && (<>
                        {GameHelper.getTeamOpenSeats(game, 0).map((seat, idx) => (
                            <TouchableOpacity
                                onPress={() => onPress(seat)}
                                key={idx}
                                disabled={!(isOpenGame && !isGameStarted) || isViewerInGame || disabled}
                                pointerEvents={!(isOpenGame && !isGameStarted) || isViewerInGame ? "none" : undefined}
                                style={{...ownStyles.playerBadge, backgroundColor: 'transparent', marginRight: 2 }}>
                                <StandardText style={{
                                    color: 'white',
                                    weight: 500,
                                }}>
                                    +
                                </StandardText>
                            </TouchableOpacity>
                        ))}
                    </>)}

                    <View style={{marginStart: 5, marginEnd: 5}}>
                        <StandardText style={{weight: 500, ...(isOpenGame && !isGameStarted ? {color: 'white'} : {})}}>vs</StandardText>
                    </View>

                    {rightTeam.teamPlayers.map((player) => (
                        <View style={{ marginLeft: 2 }} key={player.id}>
                            <PlayerBadge
                                player={player}
                                showBottomInfo={false}
                                active={player.isCurrentTurn || (isOpenGame && !isGameStarted && GameHelper.isOrganizingTeamPlayer(game, player))}
                                activePlainColor={isOpenGame && !isGameStarted && GameHelper.isOrganizingTeamPlayer(game, player) ? organizerColor : itemColor}
                                bare={isOpenGame && !isGameStarted && !GameHelper.isOrganizingTeamPlayer(game, player)}
                                popupEnabled={enablePopups}
                                animate={!(isOpenGame && !isGameStarted)}
                                menuDisplayNameSubtitle={isOpenGame && !isGameStarted && GameHelper.isOrganizingTeamPlayer(game, player) ? 'Game Organizer' : null}
                            />
                        </View>
                    ))}

                    {(isOpenGame && !isGameStarted) && (<>
                        {GameHelper.getTeamOpenSeats(game, 1).map((seat, idx) => (
                            <TouchableOpacity
                                onPress={() => onPress(seat)}
                                disabled={!(isOpenGame && !isGameStarted) || isViewerInGame || disabled}
                                pointerEvents={!(isOpenGame && !isGameStarted) || isViewerInGame ? "none" : undefined}
                                key={idx}
                                style={{...ownStyles.playerBadge, backgroundColor: 'transparent', marginLeft: 2 }}>
                                <StandardText style={{
                                    color: 'white',
                                    weight: 500,
                                }}>
                                    +
                                </StandardText>
                            </TouchableOpacity>
                        ))}
                    </>)}

                    <View style={{marginStart: 10, marginEnd: 5}}><ChevronIcon size={10} color={disablePress ? 'rgba(255, 255, 255, 0)' : 'white'}/></View>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const ownStyles = {
    itemContainer: {
        flexDirection: 'row',
        borderRadius: 2,
        padding: 7,
        marginBottom: 12,
        backgroundColor: 'rgba(248, 121, 80, 1)',
        overflow: 'hidden',
        height: 54
    },
    openGameContainer: {
        backgroundColor: 'rgba(94, 142, 255, 1)'
    },
    currentTurnContainer: {
        backgroundColor: 'rgba(136, 80, 255, 1)'
    },
    dailyTournamentGameContainer: {
        backgroundColor: Colors.tournamentColor
    },
    itemText: {
        fontSize: 14,
        weight: 500,
        color: Colors.onPrimary,
        lineHeight: 20
    },
    itemTextSmallMuted: {
        fontSize: 12,
        weight: 500,
        color: Colors.onPrimary,
        opacity: 0.85,
        lineHeight: 20,
    },
    itemTextSub: {
        fontSize: 16
    },
    playerBadge: {
        width: 36,
        height: 36,
        borderRadius: 100,
        borderWidth: 2,
        borderColor: 'white',
        justifyContent: 'center',
        alignItems: 'center'
    },
    yourTurnButton: {
        backgroundColor: 'white',
        borderRadius: 100,
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 15,
        paddingRight: 15,
    },
};

export default GameItem;
